import { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SelectMenu from '../../forms/SelectMenu';
import TextInput from '../../forms/TextInput';
import {
  PurposeCodeModel,
  PurposeCodeModelType,
} from '../../../data/models/purpose-code-model.model';

const PREFIX = 'PurposeCodeModelDialog';

const classes = {
  content: `${PREFIX}-content`,
  form: `${PREFIX}-form`,
  provider: `${PREFIX}-provider`,
  providerName: `${PREFIX}-providerName`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 350,
  },
  [`& .${classes.provider}`]: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  [`& .${classes.providerName}`]: {
    flexGrow: 1,
  },
}));

interface PurposeCodeModelDialogProps {
  open: boolean;
  purposeCodeModel?: PurposeCodeModel;
  isDuplicate?: boolean;
  purposeCodeModelTypes: PurposeCodeModelType[];
  close: (saved: boolean) => void;
}

export default function PurposeCodeModelDialog({
  open,
  purposeCodeModel,
  isDuplicate = false,
  purposeCodeModelTypes,
  close,
}: PurposeCodeModelDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  // const saving = useAppSelector(userSlice.selectSavingUser);
  // const saved = useAppSelector(userSlice.selectSavedUser);
  // const savedMessage = useAppSelector(uiSlice.selectSuccessMessage);

  const [title, setTitle] = useState(t('purposeCodes.createPurposeCodeModel'));

  const { control, formState, handleSubmit, reset } = useForm();

  useEffect(() => {
    if (purposeCodeModel) {
      const purposeCodeModelType = purposeCodeModel.purposeCodeModelTypeBC;
      const name = purposeCodeModel.name;
      if (isDuplicate) {
        setTitle(t('purposeCodes.duplicatePurposeCodeModel'));
        reset({
          name: `${t('purposeCodes.duplicateOf')} ${name}`,
          purposeCodeModelType,
        });
      } else {
        setTitle(t('purposeCodes.updatePurposeCodeModel'));
        reset({ name, purposeCodeModelType });
      }
    } else {
      setTitle(t('purposeCodes.createPurposeCodeModel'));
      reset({ name: '', purposeCodeModelType: '' });
    }
  }, [purposeCodeModel, isDuplicate]);

  const savePurposeCodeModel = (data: any) => {
    console.log('savePurposeCode', data);
    // TODO: this is waiting for back end support for purpose codes
    // const purposeCodeModel = getPurposeCodeModel(data);
    // if (!purposeCodeModel) {
    //   dispatch(someSlice.createPurposeCodeAsync(newUser));
    // } else {
    //   dispatch(someSlice.updatePurposeAsync(newUser));
    // }
  };

  const closeDialog = (saved: boolean) => {
    close(saved);
  };

  const cancelDialog = () => {
    closeDialog(false);
  };

  return (
    <RootDialog
      className={classes.content}
      maxWidth="md"
      open={open}
      onClose={closeDialog}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box className={classes.content}>
          <Box className={classes.form}>
            <TextInput
              name="name"
              label={t('purposeCodes.name')}
              control={control}
              required={true}
              rules={{ required: requiredFieldMessage }}
              inputProps={{ maxLength: 50 }}
            />
            <SelectMenu
              label={t('purposeCodes.type')}
              name="purposeCodeModelType"
              values={purposeCodeModelTypes}
              valueAttr="purposeCodeModelTypeBC"
              labelAttr="purposeCodeModelTypeName"
              required={true}
              control={control}
              rules={{ required: requiredFieldMessage }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancelDialog} variant="contained" color="secondary">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit(savePurposeCodeModel)}
          variant="contained"
          color="primary"
          disabled={!formState.isValid}>
          {purposeCodeModel ? t('common.update') : t('common.create')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
